import { type FC, type ReactNode, useState } from 'react'
import { Button } from '../../../components/Button/Button'
import { type AccountOpeningCollectionStep } from '../../../models/AccountOpeningDescriptor'
import { CollectionItem } from './CollectionItem'
import { itemsOf, type CollectionContext, type ItemGroup, type ResolvedItem } from '../domain/collections'
import { HintText, PrefillButton, StepConfirmBar, StepFooter } from './flowPrimitives'
import { LoadingScreen } from './LoadingScreen'
import { collectionCopy, confirmCopy, missingFieldsText } from '../descriptor/stepCopy'
import { useStepPrefill } from '../hooks/useStepPrefill'
import { useWizardFlow } from '../hooks/WizardFlowContext'
import { type ItemPatch } from '../domain/types'

interface CollectionStepProps {
  step: AccountOpeningCollectionStep
  collectionCtx: CollectionContext
  isSaving: boolean
  isFetching: boolean
  gated: boolean
  missingGroups: ItemGroup[]
  confirmedRefs: string[]
  canDiscard: boolean
  onItemChange: (item: ResolvedItem, patch: ItemPatch) => void
  onValuesFilled: (filled: Map<string, Record<string, unknown>>) => void
  onConfirmItem: (item: ResolvedItem) => void
  onConfirmAll: () => void
  onDiscardChanges: () => void
}

export const CollectionStep: FC<CollectionStepProps> = ({
  step,
  collectionCtx,
  isSaving,
  isFetching,
  gated,
  missingGroups,
  confirmedRefs,
  canDiscard,
  onItemChange,
  onValuesFilled,
  onConfirmItem,
  onConfirmAll,
  onDiscardChanges,
}) => {
  const { readOnly } = useWizardFlow()
  const [expandedRefs, setExpandedRefs] = useState<string[]>([])
  const copy = collectionCopy(step)
  const confirm = confirmCopy(step)

  const items = itemsOf(step.collection, collectionCtx)
  const isBlockScope = step.confirm?.scope === 'block'

  const fill = useStepPrefill({ step, items, onFilled: onValuesFilled })

  if (gated) {
    return <HintText>{copy.gatedHint}</HintText>
  }

  if (isFetching) {
    return <LoadingScreen message={copy.loading} compact={step.itemLayout === 'row'} />
  }

  if (items.length === 0) {
    return <HintText>{copy.empty}</HintText>
  }

  const missingByRef = new Map(missingGroups.map((group) => [group.key, group.items]))
  const allComplete = items.every((item) => (missingByRef.get(item.ref) ?? []).length === 0)
  const allConfirmed = items.every((item) => confirmedRefs.includes(item.ref))

  const toggleExpanded = (ref: string) => {
    setExpandedRefs((previous) => (previous.includes(ref) ? previous.filter((candidate) => candidate !== ref) : [...previous, ref]))
  }

  const handleConfirmItem = (item: ResolvedItem) => {
    onConfirmItem(item)
    setExpandedRefs((previous) => previous.filter((ref) => ref !== item.ref))
  }

  const blockPrefill = isBlockScope && fill.canFillAll ? <PrefillButton label={fill.label} loading={fill.isFillingAll} onClick={fill.runAll} /> : null

  const blockConfirm =
    isBlockScope && !readOnly && !allConfirmed ? (
      <StepConfirmBar
        message={allComplete ? confirm.readyMessage : confirm.blockedMessage}
        label={confirm.label}
        disabled={isSaving || !allComplete}
        loading={isSaving}
        discard={
          confirm.discardLabel && canDiscard ? (
            <Button variant='secondary' disabled={isSaving} onClick={onDiscardChanges}>
              {confirm.discardLabel}
            </Button>
          ) : null
        }
        prefill={blockPrefill}
        onConfirm={onConfirmAll}
      />
    ) : null

  const itemFooter = (item: ResolvedItem): ReactNode => {
    if (isBlockScope) {
      return null
    }
    const missing = missingByRef.get(item.ref) ?? []
    const complete = missing.length === 0
    const confirmed = confirmedRefs.includes(item.ref)
    const prefill = fill.canFill(item) ? <PrefillButton label={fill.label} loading={fill.isFilling(item)} onClick={() => fill.runFor(item)} /> : null

    const itemConfirm =
      step.confirm?.scope === 'item' && !readOnly && !confirmed ? (
        <StepConfirmBar
          message={
            complete
              ? confirm.readyMessage
              : missingFieldsText(
                  confirm.itemBlockedMessage,
                  missing.map((entry) => entry.label),
                )
          }
          label={confirm.label}
          disabled={!complete}
          prefill={prefill}
          onConfirm={() => handleConfirmItem(item)}
        />
      ) : null

    return <StepFooter confirm={itemConfirm} prefill={prefill} error={fill.errorFor(item)} />
  }

  return (
    <div className={step.itemLayout === 'card' ? 'd-flex flex-column zocks-gap-1.5' : 'd-flex flex-column zocks-gap-1'}>
      {items.map((item) => (
        <CollectionItem
          key={item.ref}
          step={step}
          item={item}
          missing={missingByRef.get(item.ref) ?? []}
          confirmed={confirmedRefs.includes(item.ref)}
          expanded={expandedRefs.includes(item.ref)}
          footer={itemFooter(item)}
          onToggleExpand={() => toggleExpanded(item.ref)}
          onChange={(patch) => onItemChange(item, patch)}
        />
      ))}

      <StepFooter confirm={blockConfirm} prefill={blockPrefill} error={isBlockScope ? fill.error : null} />
    </div>
  )
}
